import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PadButton, WrapperA } from '../../../components/Pad/Pad'
import { selectCurrentCart } from '../storage/cart.slice'
import { Badge, Box, Stack, styled, SxProps, useMediaQuery, useTheme } from '@mui/material'

import { selectCanUseQuickOrder } from '../../azure-b2c/storage'
import { TestIds } from '../../../testIds'
import logo from '../../../assets/images/FarmlandsProLogoColored.svg'
import MaterialSymbol from '../../../components/MaterialSymbol'
import Typography from '../../../components/Typography'
import { colors } from '../../../themes/colors'
import { CartDeliveryMode } from '../storage/cart.types'
import { usePickUpStoreModal } from './PickUpStoreModal'
import onClickDeveloper from '../../../util/exportAuth'
import { ATOMIC_STREAM_INBOX_ID, useAtomic } from '../../../services/atomic/AtomicProvider'
import { selectConfigByKey } from '../../../services/firebase/remote-config/remoteConfig.slice'

const CartContentContainer = styled('div')`
  flex: 1;
  margin-top: auto;
`
const ButtonsContainer = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  flex: 1,
  justifyContent: 'space-between',
  color: colors.shade0,
  [theme.breakpoints.down('md')]: {
    color: colors.primary500,
  },
}))
const LogoWrapper = styled('div')`
  display: flex;
`
const Logo = styled('img')`
  width: 109px;
`

const PadButtonCustomStyle = {
  p: 1,
  gap: 0.5,
  minWidth: '0px',
  margin: 'auto',
  color: 'inherit',
  position: 'relative',
}

const DeliveryMode = styled(Typography)(({ theme }) => ({
  fontWeight: '700',
  color: 'inherit',
  textAlign: 'left',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}))

const StyledBadge = styled(Badge)(({ theme }) => ({
  zIndex: -1,
  position: 'absolute',
  top: 0,
  right: 0,
  '& span': {
    fontWeight: 'bold',
  },
  '& .MuiBadge-badge': {
    backgroundColor: 'white',
    color: theme.colors.primary500,
    [theme.breakpoints.down('md')]: {
      backgroundColor: theme.colors.primary500,
      color: 'white',
    },
  },
}))

const DeliveryAddress = styled(Typography)(({ theme }) => ({
  fontWeight: '400',
  color: 'inherit',
  textAlign: 'left',
  textDecoration: 'underline',
  maxWidth: '30vw',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('md')]: {
    maxWidth: 'calc(80vw - 180px)',
  },
}))

interface Props {
  sx?: SxProps
}
export const CartHeader = (props: Props) => {
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const currentCart = useSelector(selectCurrentCart)
  const navigate = useNavigate()
  const canUse = useSelector(selectCanUseQuickOrder)

  const { messageCount } = useAtomic()

  const modal = usePickUpStoreModal()

  const renderCartIcon = () => {
    return (
      <PadButton
        shade='transparent'
        isEditEnabled
        sx={PadButtonCustomStyle}
        onClick={() => navigate('/products/cart')}
        data-testid={TestIds.components.cartHeader.cartShortcutButton}
      >
        <MaterialSymbol sx={{ fontWeight: 400, position: 'relative' }}>
          shopping_cart
          <StyledBadge badgeContent={currentCart?.items.length ?? 0} />
        </MaterialSymbol>
      </PadButton>
    )
  }

  const atomic = useAtomic()
  const atomicEnabled = useSelector(selectConfigByKey('atomicEnabled'))

  const renderNotificationsIcon = () => {
    if (atomicEnabled) {
      return (
        <PadButton
          shade='transparent'
          isEditEnabled
          sx={PadButtonCustomStyle}
          onClick={() => atomic.setDrawerOpen(ATOMIC_STREAM_INBOX_ID)}
          data-testid={TestIds.components.cartHeader.inboxButton}
        >
          <MaterialSymbol sx={{ fontWeight: 400, position: 'relative', zIndex: 1 }}>
            notifications
            {messageCount > 0 && <StyledBadge badgeContent={messageCount} />}
          </MaterialSymbol>
        </PadButton>
      )
    }
  }

  const renderLogo = () => {
    if (isMd)
      return (
        <LogoWrapper>
          <Logo src={logo} alt='Farmlands logo' onClick={() => onClickDeveloper()} />
        </LogoWrapper>
      )
  }

  const renderPickupOrDelivery = React.useMemo(() => {
    const deliveryMode = currentCart?.cartDeliveryMode
    const icon = deliveryMode === CartDeliveryMode.DELIVERY ? 'local_shipping' : 'location_on'

    let mode = 'Pickup / delivery'
    let value = isMd ? 'Pickup / delivery' : 'Set delivery method'

    if (deliveryMode === CartDeliveryMode.PICKUP && currentCart?.pickupStore?.orgUnitName) {
      mode = 'Pickup'
      value = currentCart?.pickupStore?.orgUnitName
    } else if (
      deliveryMode === CartDeliveryMode.DELIVERY &&
      currentCart?.deliveryDetails?.address
    ) {
      mode = 'Delivery'
      value = currentCart?.deliveryDetails?.address?.replace(/\n/g, ', ') ?? ''
    }

    return (
      <PadButton
        shade='transparent'
        isEditEnabled
        sx={PadButtonCustomStyle}
        onClick={() => modal.setOpen(true)}
        data-testid={TestIds.components.cartHeader.pickupDeliveryShortcutButton}
      >
        <MaterialSymbol sx={{ fontWeight: 400 }}>{icon}</MaterialSymbol>
        <WrapperA>
          <DeliveryMode variant={'body2'}>{mode}</DeliveryMode>
          <DeliveryAddress variant='body2'>{value}</DeliveryAddress>
        </WrapperA>
      </PadButton>
    )
  }, [isMd, currentCart])

  return (
    <CartContentContainer {...props}>
      <Stack gap={1}>
        <Box width='100%' display='flex' justifyContent={'center'}>
          {canUse && (
            <ButtonsContainer>
              <Stack direction='row'>
                {renderLogo()}
                {renderPickupOrDelivery}
              </Stack>
              <Stack direction='row' spacing={isMd ? 0 : 1} mr={isMd ? 0 : 3} ml={isMd ? 0 : 2}>
                {renderNotificationsIcon()}
                {renderCartIcon()}
              </Stack>
            </ButtonsContainer>
          )}
        </Box>
      </Stack>
    </CartContentContainer>
  )
}

export default CartHeader
